import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 overflow-y-auto">
      <div className="max-w-4xl mx-auto bg-white shadow-sm rounded-lg p-6 sm:p-8">
        <div className="text-left">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4">Privacy Policy for KidQuotes</h1>

          <p className="text-sm text-gray-600 mb-8"><strong>Last Updated: January 24, 2025</strong></p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Introduction</h2>
          <p className="mb-4">KidQuotes ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use the KidQuotes mobile application (the "App").</p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Information We Collect</h2>

          <h3 className="text-lg font-semibold mt-4 mb-2">Personal Information You Provide</h3>
          <p>When you use KidQuotes, you may provide:</p>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>Account Information</strong>: Email address and password for authentication</li>
            <li><strong>Children's Information</strong>: Names, birthdates, and optional photos of your children</li>
            <li><strong>Quotes and Memories</strong>: Text quotes, dates, and optional context you record</li>
            <li><strong>Milestones</strong>: Child milestone information you choose to track</li>
            <li><strong>Family Information</strong>: Family group name and email addresses of family members you invite</li>
          </ul>

          <h3 className="text-lg font-semibold mt-4 mb-2">Automatically Collected Information</h3>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>Usage Data</strong>: Information about how you use the App</li>
            <li><strong>Device Information</strong>: Device type, operating system version</li>
            <li><strong>Authentication Data</strong>: Login timestamps and session information</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">How We Use Your Information</h2>
          <p className="mb-2">We use the information we collect to:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Provide and maintain the KidQuotes service</li>
            <li>Store and organize your children's quotes and memories</li>
            <li>Enable family sharing features when you choose to use them</li>
            <li>Authenticate your account and prevent unauthorized access</li>
            <li>Improve and optimize the App experience</li>
            <li>Respond to your support requests</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Data Storage and Security</h2>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>Storage</strong>: Your data is stored securely using Supabase, a secure cloud database platform</li>
            <li><strong>Encryption</strong>: All data transmission is encrypted using industry-standard SSL/TLS</li>
            <li><strong>Photos</strong>: Photos you upload are stored in secure cloud storage with access controls</li>
            <li><strong>Access Control</strong>: Only you and family members you explicitly invite can access your data</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Data Sharing and Disclosure</h2>
          <p className="font-semibold mb-4">We do not sell, trade, or rent your personal information to third parties.</p>
          <p>We may share your information only in the following circumstances:</p>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>Family Sharing</strong>: When you invite family members to your family group, they can view quotes, children, and milestones you've added</li>
            <li><strong>Service Providers</strong>: With Supabase for secure data storage and authentication</li>
            <li><strong>Legal Requirements</strong>: If required by law or to protect our rights</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Your Rights and Choices</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>Access</strong>: View all data we have stored about you</li>
            <li><strong>Update</strong>: Modify or correct your information at any time</li>
            <li><strong>Delete</strong>: Request deletion of your account and all associated data</li>
            <li><strong>Export</strong>: Download your data in a portable format</li>
            <li><strong>Control Sharing</strong>: Choose whether to participate in family sharing features</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Contact Us</h2>
          <p>If you have questions or concerns about this Privacy Policy or our data practices, please contact us:</p>
          <p className="mt-4">
            <strong>Email</strong>: kidquotesapp@gmail.com<br />
            <strong>App Support</strong>: Via Settings &gt; Support &amp; About in the KidQuotes app
          </p>

          <p className="mt-4">You can also:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Report bugs via Settings &gt; Support &amp; About &gt; Report a Bug</li>
            <li>Send feedback via Settings &gt; Support &amp; About &gt; Send Feedback</li>
            <li>Contact support directly via Settings &gt; Support &amp; About &gt; Contact Support</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Data Subject Requests</h2>
          <p>To exercise your rights to access, correct, or delete your data:</p>
          <ol className="list-decimal pl-6 mb-4">
            <li>Log into the KidQuotes app</li>
            <li>Go to Settings &gt; Data Management</li>
            <li>Use the export, import, or delete features to manage your data</li>
            <li>For specific requests or questions, contact us via Settings &gt; Support &amp; About &gt; Contact Support</li>
          </ol>
          <p>We will respond to your request within 30 days.</p>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              <strong>KidQuotes</strong><br />
              Version 1.0.0<br />
              © 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
